/**
 * Local storage plus a background copy in the cloud.
 *
 * Deliberately a wrapper rather than a replacement: `LocalStorageAdapter`
 * stays the source of truth, every read is answered from disk at disk speed,
 * and the app works identically with the network unplugged or sync switched
 * off. Sync is an afterthought bolted to the write path — which is the only
 * shape that keeps a checklist usable with a phone in one hand and bad signal.
 *
 * It is also why this adapter is constructed unconditionally: `attach()` in
 * the progress store memoises itself and can only run once per page load, so
 * an adapter that appeared when sync was switched on could never be attached.
 * This one is always present and simply has nothing to do until it has a code.
 */

import type {
  BackupV1,
  Profile,
  ProgressPatch,
  ProgressSnapshot,
} from '@/domain/progress.types';
import type { SyncEngine } from '@/sync/syncEngine';
import type { StorageAdapter, Unsubscribe } from './StorageAdapter';

export class HybridAdapter implements StorageAdapter {
  readonly id = 'hybrid' as const;
  readonly capabilities = { multiProfile: true, remote: true, realtime: false };

  constructor(
    private readonly local: StorageAdapter,
    private readonly engine: SyncEngine,
  ) {}

  init(): Promise<void> {
    return this.local.init();
  }

  listProfiles(): Promise<Profile[]> {
    return this.local.listProfiles();
  }

  getProgress(profileId: string): Promise<ProgressSnapshot> {
    return this.local.getProgress(profileId);
  }

  exportBackup(): Promise<BackupV1> {
    return this.local.exportBackup();
  }

  /* ---- writes: local first, then tell the engine there is something to send ---- */

  private async pushing<T>(work: Promise<T>): Promise<T> {
    const result = await work;
    this.engine.schedulePush();
    return result;
  }

  async createProfile(name: string): Promise<Profile> {
    return this.pushing(this.local.createProfile(name));
  }

  async renameProfile(id: string, name: string): Promise<void> {
    return this.pushing(this.local.renameProfile(id, name));
  }

  /**
   * Deleting a profile is local-only in effect: the merge is a union, so the
   * next pull from a device that still has it brings it back. Documented in
   * the Settings copy rather than papered over — tombstones would mean a
   * progress-model migration for a case that comes up once a season.
   */
  async deleteProfile(id: string): Promise<void> {
    return this.pushing(this.local.deleteProfile(id));
  }

  async duplicateProfile(id: string, name: string): Promise<Profile> {
    return this.pushing(this.local.duplicateProfile(id, name));
  }

  async applyPatches(profileId: string, patches: ProgressPatch[]): Promise<ProgressSnapshot> {
    return this.pushing(this.local.applyPatches(profileId, patches));
  }

  async clearSet(profileId: string, setId: string): Promise<ProgressSnapshot> {
    return this.pushing(this.local.clearSet(profileId, setId));
  }

  async pruneOrphans(
    profileId: string,
    liveIdsBySet: Record<string, string[]>,
  ): Promise<ProgressSnapshot> {
    return this.pushing(this.local.pruneOrphans(profileId, liveIdsBySet));
  }

  async importBackup(backup: BackupV1, mode: 'replace' | 'merge'): Promise<void> {
    return this.pushing(this.local.importBackup(backup, mode));
  }

  /**
   * Cross-tab updates only, exactly as before.
   *
   * A pull is deliberately NOT re-emitted here: it can replace the whole
   * profile list, and this callback can only deliver one profile's snapshot.
   * The progress store subscribes to the engine directly and re-reads
   * everything — see `adopt`.
   */
  subscribe(profileId: string, cb: (snapshot: ProgressSnapshot) => void): Unsubscribe {
    return this.local.subscribe(profileId, cb);
  }
}
